import { useEffect, useRef, useState } from "react";
import { useFillHeight } from "../../grid/useFillHeight";
import { ReactComponent as MenuIcon } from "../../assets/img/grid-menu.svg";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { formatDate, formatDateTime } from "../../helper/dateFormatter";
import { createClinicalFormsApi } from "../../api-helpers/ClinicalFormsApiHelper";
import {
  AssignFormRequest,
  ClinicalFormInfo,
  GridDataResponse,
  MemberFormInfo,
  PatientGridAssessment,
} from "clinical-forms-experience-client";
import AssessmentDetailsView from "./AssessmentDetailsView";
import { ClinicalFormsConstants } from "../ClinicalForms/ClinicalFormsConstants";
import EditAssessmentView from "./EditAssessmentView";
import AssessmentHeader, { AssessmentFilters } from "./AssessmentHeader";
import { useParams, useSearchParams } from "react-router-dom";
import Toaster, { ToastConfig } from "../../components/Toaster";
import { ErrorTypes } from "../../interfaces/errors";
import { useDispatch } from "react-redux";
import { setPageTitle } from "../../store/slices/pageTitleSlice";
import { LoadingHelper } from "../../components/loader/LoadingHelper";
import { Popover } from "react-bootstrap";
import { OverlayTrigger } from "react-bootstrap";
import React from "react";
import { AgGridReact } from "ag-grid-react";

interface Data {
  latestMemberFormId: string;
  assessmentTypeId: string;
  lastStatus: string;
  lastSubmittedAt: string | null;
}

// Extend the ICellRendererParams interface from AG-Grid
interface ViewIconParams extends ICellRendererParams {
  data: Data;
}

export const AssessmentGrid = () => {
  const clinicalformsApi = createClinicalFormsApi();
  const { patientId } = useParams<{ patientId: string }>();
  const { ref: gridContainerRef, height: gridHeight } = useFillHeight<HTMLDivElement>();
  const [gridReloadKey, setGridReloadKey] = useState(0);
  const [modalShow, setModalShow] = useState(false);
  const [filters, setFilters] = useState<AssessmentFilters>({
    searchText: "",
    completedBy: [],
    assessmentType: [],
    assessmentStatus: [],
  });
  const [selectedAssessment, setSelectedAssessment] =
    useState<MemberFormInfo | null>(null);
  const [searchParams] = useSearchParams();
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState<ToastConfig>({
    type: "success" as ErrorTypes,
    title: "",
    icon: "",
  });
  const assessmentId = searchParams.get("assessmentId");
  const dispatch = useDispatch();
  const [data, setData] = useState<PatientGridAssessment[]>([]);
  const [clinicalforms, setClinicalForms] = useState<ClinicalFormInfo[]>([]);

  const getClinicalForms = async () => {
    try {
      const clinicalForms: ClinicalFormInfo[] = (
        await clinicalformsApi.clinicalFormsControllerGetClinicalForms(
          ClinicalFormsConstants.assessmentType
        )
      ).data;
      setClinicalForms(clinicalForms);
      return clinicalForms;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  useEffect(() => {
    setModalShow(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [gridReloadKey]);

  useEffect(() => {
    const loadClinicalForms = async () => {
      if (!modalShow) {
        return;
      }
      try {
        LoadingHelper.showLoader(".main");
        const clinicalForms = await getClinicalForms();
        const selectedForm = clinicalForms?.filter(
          (form: ClinicalFormInfo) => form.order === 1
        );
        if (selectedForm && selectedForm.length) {
          const patientAssessments =
            await clinicalformsApi.clinicalFormsControllerGetMemberForms(
              patientId!,
              ClinicalFormsConstants.assessmentType
            );
          const existingDraftAssessment = patientAssessments.data.filter(
            (assessment) =>
              assessment.formId === selectedForm[0].id &&
              assessment.status === ClinicalFormsConstants.draftStatus
          );
          if (existingDraftAssessment.length) {
            setSelectedAssessment(existingDraftAssessment[0]);
          } else {
            assignClinicalForm(selectedForm[0].id);
          }
        }
      } catch (error) {
        console.error("Error loading clinicalforms: ", error);
      } finally {
        LoadingHelper.hideLoader(".main");
      }
    };
    loadClinicalForms();
  }, [modalShow]);

  const handleAddPgiForm = async (toasterConfig: ToastConfig) => {
    setToastConfig(toasterConfig);
    setShowToast(true);
  };

  const assignClinicalForm = async (clinicalFormId: string) => {
    try {
      const request: AssignFormRequest = {
        formId: clinicalFormId,
        source: !assessmentId ? "manual" : "appointment",
        sourceRefId: undefined,
      };
      const memberForm: MemberFormInfo = (
        await clinicalformsApi.clinicalFormsControllerAssignClinicalForm(
          request,
          patientId!
        )
      ).data;
      onAssignAsssessment(memberForm);
    } catch (error) {
      console.error("Error assigning clinicalform: ", error);
    }
  };

  const ViewIcon = (props: ViewIconParams) => {
    const clinicalformsApi = createClinicalFormsApi();
    const { patientId } = useParams<{ patientId: string }>();
    const [searchParams] = useSearchParams();
    const assessmentId = searchParams.get("assessmentId");
    const [show, setShow] = useState(false);
    const iconRef = useRef<HTMLDivElement | null>(null);

    const handleClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      setShow(!show);
    };

    const pastAssessments = async () => {
      try {
        const getlastros =
          await clinicalformsApi.clinicalFormsControllerGetMemberForms(
            patientId!,
            ClinicalFormsConstants.assessmentType
          );

        const submittedForms = getlastros.data.filter(
          (form) =>
            form.status === ClinicalFormsConstants.submittedStatus &&
            form.formId === props.data.assessmentTypeId
        );

        if (submittedForms.length === 0) {
          setShowToast(true);
          setToastConfig({
            type: "warning" as ErrorTypes,
            title: "No past assessment found!",
            icon: "error_outline",
          });
          return null;
        }
        const sortedSubmittedForms = submittedForms.sort((a, b) => {
          return (
            new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
          );
        });

        const memberForm = (
          await clinicalformsApi.clinicalFormsControllerGetMemberForm(
            sortedSubmittedForms[0].id
          )
        ).data;

        getClinicalForms();
        setSelectedAssessment(memberForm);
      } catch (error) {
        console.error(error);
      }
    };

    const openNewAssessment = async (event: React.MouseEvent) => {
      event.stopPropagation();
      const request: AssignFormRequest = {
        formId: props.data.assessmentTypeId,
        source: !assessmentId ? "manual" : "appointment",
        sourceRefId: undefined,
      };
      const memberForm: MemberFormInfo = (
        await clinicalformsApi.clinicalFormsControllerAssignClinicalForm(
          request,
          patientId!
        )
      ).data;
      getClinicalForms();
      setSelectedAssessment(memberForm);
    };

    const resumeAssessment = async () => {
      const memberForm =
        await clinicalformsApi.clinicalFormsControllerGetMemberForm(
          props.data.latestMemberFormId
        );
      getClinicalForms();
      setSelectedAssessment(memberForm.data);
    };

    useEffect(() => {
      const handleOutsideClick = (event: MouseEvent) => {
        if (
          iconRef.current &&
          !iconRef.current.contains(event.target as Node) &&
          !(event.target as HTMLElement).closest(".custom-popover")
        ) {
          setShow(false);
        }
      };

      document.addEventListener("mousedown", handleOutsideClick);
      return () => {
        document.removeEventListener("mousedown", handleOutsideClick);
      };
    }, []);

    return (
      <OverlayTrigger
        trigger="click"
        placement="left"
        show={show}
        overlay={
          <Popover id="popover-basic" className="custom-popover">
            <div className="popover-content">
              {props.data.lastStatus === "draft" && (
                <button className="popover-button" onClick={resumeAssessment}>
                  Resume
                </button>
              )}
              {props.data.lastStatus !== "draft" && (
                <button className="popover-button" onClick={openNewAssessment}>
                  New
                </button>
              )}
              <button className="popover-button" onClick={pastAssessments}>
                Past Assessments
              </button>
            </div>
          </Popover>
        }
      >
        <div ref={iconRef} onClick={handleClick} role="button">
          <MenuIcon />
        </div>
      </OverlayTrigger>
    );
  };

  const colDefs: ColDef[] = [
    {
      field: "assessmentType",
      minWidth: 400,
      maxWidth: 900,
      headerName: "Assessment Type",
      pinned: "left",
      sortable: true,
    },
    {
      field: "lastScore",
      minWidth: 150,
      maxWidth: 180,
      headerName: "Last Score",
      pinned: "left",
      sortable: true,
      valueFormatter: (params) => params.data.lastScore ?? "-",
    },
    {
      field: "lastSubmittedAt",
      minWidth: 260,
      maxWidth: 300,
      headerName: "Last Submitted On",
      valueFormatter: (params) => formatDateTime(params.data.lastSubmittedAt),
      sortable: true,
    },
    {
      field: "lastCompletedBy",
      minWidth: 260,
      maxWidth: 300,
      headerName: "Last Completed By",
      sortable: true,
      valueFormatter: (params) => params.data.lastCompletedBy?.trim() || "-",
    },
    {
      field: "nextDueDate",
      minWidth: 260,
      maxWidth: 300,
      headerName: "Next Due",
      valueFormatter: (params) => formatDate(params.data.nextDueDate),
      sortable: true,
    },
    {
      field: "lastStatus",
      minWidth: 260,
      maxWidth: 300,
      headerName: "Status",
      sortable: true,
      valueFormatter: (params) => params.data.lastStatus ?? "-",
    },
    {
      field: "",
      minWidth: 50,
      maxWidth: 60,
      cellRenderer: ViewIcon,
      pinned: "right",
      sortable: false,
    },
  ];

  const defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    editable: false,
    suppressMenu: true,
  };

  useEffect(() => {
    dispatch(setPageTitle("Assessments: Patient Profile | Spire360"));
    loadData();
  }, []);

  useEffect(() => {
    if (assessmentId) {
      startAssessment(assessmentId);
      setModalShow(true);
    }
  }, [assessmentId]);

  const onAssignAsssessment = (memberForm: MemberFormInfo) => {
    setSelectedAssessment(memberForm);
  };

  const startAssessment = async (memberFormId: string) => {
    const memberFormInfo =
      await clinicalformsApi.clinicalFormsControllerGetMemberForm(memberFormId);
    setSelectedAssessment(memberFormInfo.data);
  };

  const handleOffCanvasShow = (comp: any) => {
    if (comp === "AddAssessment") {
      setModalShow(!modalShow);
    }
  };

  const loadData = async (): Promise<GridDataResponse> => {
    const result =
      await clinicalformsApi.clinicalFormsControllerGetAssessmentsGridData(
        patientId!
      );
    const gridData = result.data;
    setData(gridData);
    return {
      data: gridData,
      total: gridData.length,
    };
  };

  const reloadGrid = () => {
    setGridReloadKey(gridReloadKey + 1);
  };

  const onSubmitPgis = async () => {
    const memberform =
      await clinicalformsApi.clinicalFormsControllerGetMemberForm(
        selectedAssessment!.id
      );
    setSelectedAssessment(memberform.data);
  };

  // Function to apply client-side filtering based on the AssessmentFilters
  const getFilteredData = () => {
    return data.filter((item) => {
      let matches = true;
      // Search Text filtering on assessment type (adjust property if needed)
      if (filters.searchText) {
        // Assuming the search should match the assessmentType field (case insensitive)
        if (
          !item.assessmentType ||
          !item.assessmentType
            .toLowerCase()
            .includes(filters.searchText.toLowerCase())
        ) {
          matches = false;
        }
      }
      // Completed By filtering on lastCompletedBy (assuming it stores the user id)
      if (filters.completedBy.length > 0) {
        if (
          !item.lastCompletedBy ||
          !filters.completedBy.includes(item.lastCompletedBy)
        ) {
          matches = false;
        }
      }
      // Assessment Type filtering (assuming item has a property assessmentMasterId)
      if (filters.assessmentType.length > 0) {
        if (
          !item.assessmentTypeId ||
          !filters.assessmentType.includes(item.assessmentTypeId)
        ) {
          matches = false;
        }
      }
      // Assessment Status filtering (using lastStatus from the grid data)
      if (filters.assessmentStatus.length > 0) {
        if (
          !item.lastStatus ||
          !filters.assessmentStatus.includes(item.lastStatus)
        ) {
          matches = false;
        }
      }
      return matches;
    });
  };

  // Use the filtered data for rendering in the grid
  const filteredData = getFilteredData();

  return (
    <>
      <div className="pgi-view row">
        <Toaster
          show={showToast}
          onClose={() => setShowToast(false)}
          type={toastConfig.type}
          title={toastConfig.title}
          message={toastConfig.message}
          icon={toastConfig.icon}
        />
      </div>
      <div className={!selectedAssessment ? "d-none" : ""}>
        {ClinicalFormsConstants.isReadOnly(selectedAssessment?.status) ? (
          <AssessmentDetailsView
            patientId={patientId!}
            memberFormId={selectedAssessment?.id}
            onClosed={() => {
              setSelectedAssessment(null);
              setModalShow(false);
            }}
          />
        ) : (
          <EditAssessmentView
            patientId={patientId!}
            memberFormId={selectedAssessment?.id}
            handleAddPgiForm={handleAddPgiForm}
            reloadGrid={reloadGrid}
            clinicalForms={clinicalforms}
            source={!assessmentId ? "manual" : "appointment"}
            offCanvasShowCallback={handleOffCanvasShow}
            fromAddAssessment={true}
            onClosed={() => {
              setSelectedAssessment(null);
              setModalShow(false);
            }}
          />
        )}
      </div>
      <div className={selectedAssessment ? "main d-none" : "main"}>
        <AssessmentHeader
          patientId={patientId}
          offCanvasShowCallback={handleOffCanvasShow}
          onUpdateFilters={setFilters}
        />
        <div className="d-flex px-3 px-md-4 px-lg-5 d-flex align-items-stretch">
          <div className="flex-fill">
            <div className="py-5">
              <div
                ref={gridContainerRef}
                className={"generic-grid ag-theme-quartz"}
                style={{ width: "100%", height: gridHeight ?? "" }}
              >
                <AgGridReact
                  columnDefs={colDefs}
                  defaultColDef={defaultColDef}
                  rowModelType="clientSide"
                  pagination={true}
                  rowData={filteredData}
                  cacheBlockSize={20}
                  suppressDragLeaveHidesColumns={true}
                  suppressColumnMoveAnimation={true}
                  suppressMovableColumns={true}
                  suppressNoRowsOverlay={false}
                />
              </div>
            </div>
          </div>
        </div>
        {/* <AssignAssessmentModal
          patientId={patientId!}
          show={modalShow}
          type={ClinicalFormsConstants.assessmentType}
          onSuccess={onAssignAsssessment}
          onHide={() => setModalShow(false)}
        ></AssignAssessmentModal> */}
      </div>
    </>
  );
};
